import { addDays, addHours, addSeconds } from 'date-fns'
import { Box, FormControl, InputLabel, MenuItem, Select, Stack } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { BookingNewBookingProps } from './types'
import { RootStore } from '../../../redux/store'
import { BookingPeriods } from './enums'
import TimePicker from '../../../shared/UI/TimePicker'
import { getBookedDurationWithinPeriod, getBookingAvailabilityInPeriod } from '../Shared/bookingFloorplanLogic'
import { BOOKING_LENGTH_OPTION_MAP, BOOKING_SLOT_INCREMENT, BookingAvailability } from '../consts'
import { combineDateAndTime } from '../utils/utils'
import { createDateFromTimeString, formatLocalDateYmd, formatTime } from '../../../utils/date-utils'
import { useBookingFor } from '../Shared/useBookingFor'
import { BookingForControls } from '../Shared/BookingForControls'

function NewBooking({
  onSelectedRange,
  onErrorStateChange,
  onBookingLengthChange,
  selectedDate,
  selectedBookingLength,
  selectedBookingRange,
  bookings,
  onBookingForChange,
  bookingFor,
  selectedEmployee,
  isManager
}: BookingNewBookingProps) {
  const { floorplanViewingDate } = useSelector((state: RootStore) => state.deskBooking)
  const [isTimeFromValid, setIsTimeFromValid] = useState(true)
  const [isTimeToValid, setIsTimeToValid] = useState(true)

  const {
    employees,
    isManagerBookingForEmployeeWithoutSelection
  } = useBookingFor({
    bookingFor,
    selectedEmployee,
    isManager
  })

  const isBookingOptionAvailable = (bookingPeriod: BookingPeriods) => {
    if (bookingPeriod === BookingPeriods.CUSTOM_TIME_SLOT) {
      return true
    }
  
    const rangeStart = combineDateAndTime(floorplanViewingDate, BOOKING_LENGTH_OPTION_MAP[bookingPeriod].from)
    const rangeEnd = combineDateAndTime(floorplanViewingDate, BOOKING_LENGTH_OPTION_MAP[bookingPeriod].to)
    const availability = getBookingAvailabilityInPeriod(rangeStart, rangeEnd, bookings)
    return availability === BookingAvailability.AVAILABLE
  }
  
  const isSelectedBookingLengthOptionInvalid = useMemo(() => !isBookingOptionAvailable(selectedBookingLength), [selectedBookingLength])

  useEffect(() => {
    onErrorStateChange(
      !selectedBookingRange.from
      || !selectedBookingRange.to
      || !isTimeFromValid
      || !isTimeToValid
      || isSelectedBookingLengthOptionInvalid
      || isManagerBookingForEmployeeWithoutSelection
    )
  }, [isTimeFromValid, isTimeToValid, isSelectedBookingLengthOptionInvalid, isManagerBookingForEmployeeWithoutSelection])

  const isCustomTimeSelected = selectedBookingLength === BookingPeriods.CUSTOM_TIME_SLOT

  return (
    <Stack spacing={2}>
      <FormControl sx={{ width: '100%' }} size="medium">
        <InputLabel id="booking-length-input-label">Booking Length</InputLabel>
        <Select
          value={selectedBookingLength}
          id="grouped-select"
          label="Booking Length"
          labelId="booking-length-input-label"
          MenuProps={{
            disableScrollLock: true,
          }}
          error={isSelectedBookingLengthOptionInvalid}
          onChange={e => {
            const selectedPeriod = e.target.value as BookingPeriods
            onBookingLengthChange(selectedPeriod)

            const selectedOption = BOOKING_LENGTH_OPTION_MAP[selectedPeriod]
            onSelectedRange({
              selectedRange: {
                from: createDateFromTimeString(selectedOption.from),
                to: createDateFromTimeString(selectedOption.to) },
              date: floorplanViewingDate,
            })
          }}
        >
          {Object.entries(BOOKING_LENGTH_OPTION_MAP).map(([key, value]) => (
            <MenuItem
              key={key}
              value={key}
              disabled={!isBookingOptionAvailable(key as BookingPeriods)}
            >
              {value.text}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {isCustomTimeSelected &&
        <Box display="flex" columnGap={2} justifyContent="space-between">
          <TimePicker
            label="From"
            value={selectedBookingRange.from}
            minutesStep={BOOKING_SLOT_INCREMENT}
            error={!isTimeFromValid || !selectedBookingRange.from}
            onChange={(newValue) => {
              const from = newValue
              const { to } = selectedBookingRange
              
              if (from && to) {
                onSelectedRange({
                  date: floorplanViewingDate,
                  selectedRange: { from, to }
                })
              }
            }}
            onError={(error) => {
              setIsTimeFromValid(error === null)
            }}
            shouldDisableTime={(value, view) => {
              if (view === 'hours') {
                // the given hour should be disabled if the full hour has already been booked
                const startDate = combineDateAndTime(selectedDate, formatTime(value))
                const endDate = addSeconds(addHours(startDate, 1), -1)
                const bookedDuration = getBookedDurationWithinPeriod(startDate, endDate, bookings)
                return bookedDuration === 60 * 60
              }

              return bookings.some(booking => booking.fromDate === formatLocalDateYmd(selectedDate) && booking.fromTime <= formatTime(value) && booking.toTime >= formatTime(value))
            }}
          />
          <TimePicker
            label="To"
            value={addSeconds(selectedBookingRange.to, 1)}
            minutesStep={BOOKING_SLOT_INCREMENT}
            error={!isTimeToValid || !selectedBookingRange.to}
            onChange={(newValue) => {
              const { from } = selectedBookingRange
              const to = newValue ? addSeconds(newValue, -1) : null

              if (from && to) {
                onSelectedRange({
                  date: floorplanViewingDate,
                  selectedRange: { from, to }
                })
              }
            }}
            onError={(error) => {
              setIsTimeToValid(error === null)
            }}
            shouldDisableTime={(value, view) => {
              const startDate = combineDateAndTime(selectedDate, selectedBookingRange.from)
              let endDate = combineDateAndTime(selectedDate, value)
              if (view === 'hours') {
                const zeroMinuteEndDate = new Date(endDate)
                zeroMinuteEndDate.setMinutes(0)
                // in the edge case where the hour can represent both a time slot on the selected date and on the next day
                // (e.g. start date is 09:30, and the end hour is 9), the smallest time slot possible
                // is 1 increment step after start date
                if (zeroMinuteEndDate <= startDate && addHours(zeroMinuteEndDate, 1) >= startDate) {
                  endDate.setMinutes(startDate.getMinutes() + BOOKING_SLOT_INCREMENT)
                } else {
                  endDate = zeroMinuteEndDate
                }
              }
              if (endDate <= startDate) {
                endDate = addDays(endDate, 1)
              }
              endDate = addSeconds(endDate, -1)
              const bookedDuration = getBookedDurationWithinPeriod(startDate, endDate, bookings)
              return bookedDuration > 0
            }}
          />
        </Box>
      }
      {isManager &&
        <BookingForControls
          bookingFor={bookingFor}
          selectedEmployee={selectedEmployee}
          employees={employees}
          onBookingForChange={onBookingForChange}
        />}
    </Stack>
  )
}

export default NewBooking
